import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';
import { Warehouse, AlertTriangle, Plus, TrendingUp, TrendingDown } from 'lucide-react';
import toast from 'react-hot-toast';
import './Estoque.css';

const Estoque = () => {
  const [abaAtiva, setAbaAtiva] = useState('produtos');
  const queryClient = useQueryClient();

  const { data: produtos } = useQuery(
    'produtos-estoque',
    async () => {
      const response = await api.get('/api/produtos');
      return response.data.data.produtos;
    }
  );

  const { data: alertas } = useQuery(
    'alertas-estoque',
    async () => {
      const estabelecimento = queryClient.getQueryData('estabelecimento')?.data?.estabelecimento?._id;
      if (!estabelecimento) return { produtos: [] };
      const response = await api.get(`/api/estoque/alertas?estabelecimento=${estabelecimento}`);
      return response.data.data;
    }
  );

  const { data: movimentacoes } = useQuery(
    'movimentacoes-estoque',
    async () => {
      const estabelecimento = queryClient.getQueryData('estabelecimento')?.data?.estabelecimento?._id;
      if (!estabelecimento) return { movimentacoes: [] };
      const response = await api.get(`/api/estoque/movimentacoes?estabelecimento=${estabelecimento}`);
      return response.data.data;
    }
  );

  return (
    <div className="estoque-page">
      <div className="page-header">
        <div>
          <h2><Warehouse size={28} /> Estoque</h2>
          <p>Gestão completa de inventário</p>
        </div>
        {alertas?.produtos?.length > 0 && (
          <div className="alert-badge">
            <AlertTriangle size={20} />
            {alertas.produtos.length} alerta(s)
          </div>
        )}
      </div>

      {alertas?.produtos?.length > 0 && (
        <div className="alertas-estoque">
          <h3>⚠️ Produtos com Estoque Baixo</h3>
          <div className="alertas-grid">
            {alertas.produtos.map(produto => (
              <div key={produto._id} className="alerta-card">
                <h4>{produto.nome}</h4>
                <p>
                  Estoque: <strong>{produto.estoque?.quantidade || 0}</strong> / 
                  Mínimo: <strong>{produto.estoque?.minimo || 0}</strong>
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="abas-estoque">
        <button
          className={`aba ${abaAtiva === 'produtos' ? 'ativa' : ''}`}
          onClick={() => setAbaAtiva('produtos')}
        >
          Produtos
        </button>
        <button
          className={`aba ${abaAtiva === 'movimentacoes' ? 'ativa' : ''}`}
          onClick={() => setAbaAtiva('movimentacoes')}
        >
          Movimentações
        </button>
        <button
          className={`aba ${abaAtiva === 'fichas' ? 'ativa' : ''}`}
          onClick={() => setAbaAtiva('fichas')}
        >
          Fichas Técnicas
        </button>
      </div>

      <div className="conteudo-aba">
        {abaAtiva === 'produtos' && (
          <div className="produtos-estoque-grid">
            {produtos?.map(produto => (
              <ProdutoEstoqueCard key={produto._id} produto={produto} />
            ))}
          </div>
        )}

        {abaAtiva === 'movimentacoes' && (
          <div className="movimentacoes-lista">
            {movimentacoes?.movimentacoes?.map(mov => (
              <div key={mov._id} className={`movimentacao-card ${mov.tipo}`}>
                <div className="mov-header">
                  <div>
                    <strong>{mov.produto?.nome}</strong>
                    <span>{new Date(mov.createdAt).toLocaleString('pt-BR')}</span>
                  </div>
                  <span className={`tipo-badge ${mov.tipo}`}>
                    {mov.tipo === 'entrada' ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                    {mov.tipo}
                  </span>
                </div>
                <div className="mov-info">
                  <div>
                    <span>Quantidade</span>
                    <strong>{mov.quantidade} {mov.produto?.estoque?.unidade || 'un'}</strong>
                  </div>
                  <div>
                    <span>Anterior</span>
                    <strong>{mov.quantidadeAnterior}</strong>
                  </div>
                  <div>
                    <span>Atual</span>
                    <strong>{mov.quantidadeAtual}</strong>
                  </div>
                </div>
                {mov.motivo && (
                  <p className="mov-motivo">{mov.motivo}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ProdutoEstoqueCard = ({ produto }) => {
  const [modalAjuste, setModalAjuste] = useState(false);
  const [quantidadeNova, setQuantidadeNova] = useState(produto.estoque?.quantidade || 0);
  const queryClient = useQueryClient();

  const ajusteMutation = useMutation(
    (data) => api.post('/api/estoque/ajuste', data),
    {
      onSuccess: () => {
        toast.success('Estoque ajustado!');
        queryClient.invalidateQueries('produtos-estoque');
        setModalAjuste(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao ajustar estoque');
      }
    }
  );

  const estoqueBaixo = produto.estoque?.quantidade <= (produto.estoque?.minimo || 0);

  return (
    <div className={`produto-estoque-card ${estoqueBaixo ? 'baixo' : ''}`}>
      <div className="produto-estoque-header">
        <h4>{produto.nome}</h4>
        {estoqueBaixo && <AlertTriangle size={20} className="alert-icon" />}
      </div>
      <div className="estoque-info">
        <div>
          <span>Quantidade</span>
          <strong>{produto.estoque?.quantidade || 0} {produto.estoque?.unidade || 'un'}</strong>
        </div>
        <div>
          <span>Mínimo</span>
          <strong>{produto.estoque?.minimo || 0}</strong>
        </div>
      </div>
      <button
        className="btn btn-secondary btn-small"
        onClick={() => setModalAjuste(true)}
      >
        Ajustar Estoque
      </button>

      {modalAjuste && (
        <div className="modal-overlay" onClick={() => setModalAjuste(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Ajustar Estoque</h3>
            <div className="form-group">
              <label>Nova Quantidade</label>
              <input
                type="number"
                min="0"
                step="1"
                value={quantidadeNova || ''}
                onChange={(e) => {
                  const valor = e.target.value;
                  if (valor === '' || valor === null || valor === undefined) {
                    setQuantidadeNova(0);
                  } else {
                    const num = parseInt(valor, 10);
                    setQuantidadeNova(isNaN(num) ? 0 : num);
                  }
                }}
              />
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setModalAjuste(false)}>
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  const estabelecimento = queryClient.getQueryData('estabelecimento')?.data?.estabelecimento?._id;
                  
                  // Validar quantidade
                  const quantidade = Number(quantidadeNova);
                  if (isNaN(quantidade) || quantidade < 0) {
                    toast.error('Por favor, insira uma quantidade válida');
                    return;
                  }
                  
                  ajusteMutation.mutate({
                    estabelecimento,
                    produto: produto._id,
                    quantidadeNova: quantidade,
                    motivo: 'Ajuste manual'
                  });
                }}
                disabled={ajusteMutation.isLoading}
              >
                {ajusteMutation.isLoading ? 'Salvando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Estoque;

